/**
 * RecordingSessionStore
 *
 * Zustand store for managing the active recording session state.
 * Tracks MediaRecorder lifecycle, pause timestamps, mic signal,
 * and minimised/maximised state for the recording UI.
 *
 * Elapsed time is NOT stored — it is computed from startedAt + pauses
 * by consuming components via getElapsedMs().
 *
 * Consumed by: Recording flow UI, mini bar, list view (disable button).
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useState, useEffect, useMemo } from 'react';

// ─── Types ─────────────────────────────────────────────────────────

interface RecordingSetupData {
  title?: string;
  participantContactIds?: number[];
}

export interface PauseRange {
  start: number;
  end?: number;
}

// ─── State ──────────────────────────────────────────────────────────

interface RecordingSessionState {
  /** Matter this recording belongs to */
  matterId: number | null;
  /** Server recording ID — set at session start from POST /recordings */
  recordingId: number | null;
  /** S3 presigned upload URL — from POST /recordings response */
  uploadUrl: string | null;
  /** Matter name for display in mini bar when on different page */
  matterName: string | null;
  /** Law firm internal reference (e.g. "REF-001") — preferred over matterName in mini bar */
  matterReference: string | null;

  /** Whether a recording session is active (modal open) */
  isRecording: boolean;
  /** Whether recording is paused */
  isPaused: boolean;
  /** Whether the full-screen recording UI is collapsed to mini bar */
  isMinimised: boolean;
  /** Epoch ms when the MediaRecorder actually started */
  startedAt: number | null;
  /** Pause ranges — each has a start, and an end when resumed */
  pauses: PauseRange[];

  /** Label of the active audio input device (e.g. "MacBook Air Microphone") */
  activeDeviceLabel: string | null;
  /** Total bytes of audio data stored so far */
  storedBytes: number;

  /** Whether the mic is detecting audio signal (RMS above baseline) */
  micSignalPresent: boolean;
  /** When signal dropped — null means signal is OK */
  micNoSignalSince: Date | null;

  /** Pre-acquired MediaStream from getUserMedia (owned by state manager) */
  stream: MediaStream | null;
  /** Setup data collected before recording started */
  setupData: RecordingSetupData | null;
  /** Flag set by mini bar to request stop — state manager picks this up */
  requestStopConfirm: boolean;

  /**
   * Recording id of an active top-level (`/scribe`) inline capture, or null.
   * The inline capture flow lives on the session page (not this store), but it
   * registers here so there is **one shared recording "brain"** app-wide: at
   * most one recording — matter modal OR inline — can be live at a time
   * (LF-3975). The matter flow refuses to open the mic while this is set, and
   * the inline flow refuses to start while `isRecording` (matter) is true.
   */
  inlineCaptureRecordingId: number | null;
}

// ─── Actions ────────────────────────────────────────────────────────

interface RecordingSessionActions {
  /** Open the recording modal — no server call yet */
  startSession: (params: {
    matterId: number;
    matterName?: string;
    matterReference?: string;
    setupData?: RecordingSetupData;
  }) => void;
  /** Store server recording ID + upload URL after POST /recordings succeeds */
  setRecording: (recordingId: number, uploadUrl: string) => void;
  /** Store the mic stream once acquired (sound check or consent step) */
  setStream: (stream: MediaStream) => void;
  /** Pause recording — pushes a new pause range */
  pause: () => void;
  /** Resume recording — closes the current pause range */
  resume: () => void;
  /** Collapse full-screen UI to mini bar */
  minimise: () => void;
  /** Expand mini bar back to full-screen UI */
  maximise: () => void;
  /** Store the active audio input device label */
  setActiveDeviceLabel: (label: string | null) => void;
  /** Add bytes to the stored audio total */
  addStoredBytes: (bytes: number) => void;
  /** Update mic signal status from AnalyserNode RMS */
  setMicSignal: (present: boolean) => void;
  /** Signal from mini bar to request stop — state manager handles the actual stop */
  setRequestStopConfirm: () => void;
  /** Clear the requestStopConfirm flag after state manager has handled it */
  clearRequestStopConfirm: () => void;
  /**
   * Release the mic — stop the stream's tracks and clear it — WITHOUT
   * resetting the rest of the session. Used as a safety net when the recorder
   * reaches a terminal state (e.g. `error`) so the browser/OS mic-in-use
   * indicator clears immediately, while the error UI stays on screen (LF-3922).
   */
  releaseStream: () => void;
  /** End the recording session, stop stream tracks, and reset all state */
  endSession: () => void;
  /** Register an active top-level inline capture (shared-brain lock, LF-3975) */
  registerInlineCapture: (recordingId: number) => void;
  /** Clear the inline-capture lock — called on every inline terminal path */
  clearInlineCapture: () => void;
}

type RecordingSessionStore = RecordingSessionState & RecordingSessionActions;

// ─── Initial State ──────────────────────────────────────────────────

const initialState: RecordingSessionState = {
  matterId: null,
  recordingId: null,
  uploadUrl: null,
  matterName: null,
  matterReference: null,
  isRecording: false,
  isPaused: false,
  isMinimised: false,
  startedAt: null,
  pauses: [],
  activeDeviceLabel: null,
  storedBytes: 0,
  micSignalPresent: false,
  micNoSignalSince: null,
  stream: null,
  setupData: null,
  requestStopConfirm: false,
  inlineCaptureRecordingId: null
};

// ─── Elapsed Time Computation ──────────────────────────────────────

/**
 * Compute elapsed active recording time in milliseconds from timestamps.
 * Subtracts all completed pause durations and the current active pause.
 */
export function getElapsedMs(
  startedAt: number | null,
  pauses: PauseRange[],
  now: number = Date.now()
): number {
  if (!startedAt) return 0;

  const totalWall = now - startedAt;

  let totalPaused = 0;
  for (const p of pauses) {
    const end = p.end ?? now;
    totalPaused += end - p.start;
  }

  return Math.max(0, totalWall - totalPaused);
}

/** Convenience: elapsed in whole seconds */
export function getElapsedSeconds(
  startedAt: number | null,
  pauses: PauseRange[],
  now?: number
): number {
  return Math.floor(getElapsedMs(startedAt, pauses, now) / 1000);
}

// ─── Store ──────────────────────────────────────────────────────────

export const useRecordingSessionStore = create<RecordingSessionStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      startSession: (params) => {
        set(
          {
            matterId: params.matterId,
            recordingId: null,
            uploadUrl: null,
            matterName: params.matterName ?? null,
            matterReference: params.matterReference ?? null,
            isRecording: true,
            isPaused: false,
            isMinimised: false,
            startedAt: null,
            pauses: [],
            activeDeviceLabel: null,
            storedBytes: 0,
            micSignalPresent: false,
            micNoSignalSince: null,
            stream: null,
            setupData: params.setupData ?? null,
            requestStopConfirm: false
          },
          false,
          'startSession'
        );
      },

      setRecording: (recordingId, uploadUrl) => {
        set({ recordingId, uploadUrl }, false, 'setRecording');
      },

      setStream: (stream) => {
        set({ stream, startedAt: Date.now() }, false, 'setStream');
      },

      pause: () => {
        set(
          (state) => ({
            isPaused: true,
            pauses: [...state.pauses, { start: Date.now() }]
          }),
          false,
          'pause'
        );
      },

      resume: () => {
        const { pauses } = get();
        const now = Date.now();
        const updated = pauses.map((p, i) =>
          i === pauses.length - 1 && !p.end ? { ...p, end: now } : p
        );
        set({ isPaused: false, pauses: updated }, false, 'resume');
      },

      minimise: () => {
        set({ isMinimised: true }, false, 'minimise');
      },

      maximise: () => {
        set({ isMinimised: false }, false, 'maximise');
      },

      setActiveDeviceLabel: (label) => {
        set({ activeDeviceLabel: label }, false, 'setActiveDeviceLabel');
      },

      addStoredBytes: (bytes) => {
        set(
          (s) => ({ storedBytes: s.storedBytes + bytes }),
          false,
          'addStoredBytes'
        );
      },

      setMicSignal: (present) => {
        const current = get();
        if (present && !current.micSignalPresent) {
          set(
            { micSignalPresent: true, micNoSignalSince: null },
            false,
            'setMicSignal/recovered'
          );
        } else if (!present && current.micSignalPresent) {
          set(
            { micSignalPresent: false, micNoSignalSince: new Date() },
            false,
            'setMicSignal/lost'
          );
        }
      },

      setRequestStopConfirm: () => {
        set({ requestStopConfirm: true }, false, 'setRequestStopConfirm');
      },

      clearRequestStopConfirm: () => {
        set({ requestStopConfirm: false }, false, 'clearRequestStopConfirm');
      },

      releaseStream: () => {
        const { stream } = get();
        if (stream) {
          stream.getTracks().forEach((t) => t.stop());
          set({ stream: null }, false, 'releaseStream');
        }
      },

      endSession: () => {
        // Stop stream tracks before resetting
        const { stream } = get();
        if (stream) {
          stream.getTracks().forEach((t) => t.stop());
        }
        set(initialState, false, 'endSession');
      },

      registerInlineCapture: (recordingId) => {
        set(
          { inlineCaptureRecordingId: recordingId },
          false,
          'registerInlineCapture'
        );
      },

      clearInlineCapture: () => {
        set({ inlineCaptureRecordingId: null }, false, 'clearInlineCapture');
      }
    }),
    {
      name: 'RecordingSessionStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);

// ─── Selector Hooks ─────────────────────────────────────────────────

export const useIsRecording = () =>
  useRecordingSessionStore((s) => s.isRecording);

export const useIsPaused = () => useRecordingSessionStore((s) => s.isPaused);

export const useIsMinimised = () =>
  useRecordingSessionStore((s) => s.isMinimised);

export const useRecordingMatterId = () =>
  useRecordingSessionStore((s) => s.matterId);

export const useRecordingMatterName = () =>
  useRecordingSessionStore((s) => s.matterName);

export const useRecordingMatterReference = () =>
  useRecordingSessionStore((s) => s.matterReference);

export const useActiveDeviceLabel = () =>
  useRecordingSessionStore((s) => s.activeDeviceLabel);

export const useStoredBytes = () =>
  useRecordingSessionStore((s) => s.storedBytes);

/** Returns true when the mini bar is visible. Only re-renders when visibility changes. */
export const useIsMiniBarVisible = () =>
  useRecordingSessionStore((s) => s.isRecording && s.isMinimised);

/**
 * Hook that computes elapsed seconds from timestamps, re-rendering
 * every second via interval. No state in the store — purely derived.
 */
export function useElapsedSeconds(): number {
  const startedAt = useRecordingSessionStore((s) => s.startedAt);
  const pauses = useRecordingSessionStore((s) => s.pauses);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  return getElapsedSeconds(startedAt, pauses, now);
}

// ─── Non-Hook Getters ───────────────────────────────────────────────

export const getRecordingSessionActions = () => ({
  startSession: useRecordingSessionStore.getState().startSession,
  setRecording: useRecordingSessionStore.getState().setRecording,
  setStream: useRecordingSessionStore.getState().setStream,
  pause: useRecordingSessionStore.getState().pause,
  resume: useRecordingSessionStore.getState().resume,
  minimise: useRecordingSessionStore.getState().minimise,
  maximise: useRecordingSessionStore.getState().maximise,
  setActiveDeviceLabel:
    useRecordingSessionStore.getState().setActiveDeviceLabel,
  addStoredBytes: useRecordingSessionStore.getState().addStoredBytes,
  setMicSignal: useRecordingSessionStore.getState().setMicSignal,
  setRequestStopConfirm:
    useRecordingSessionStore.getState().setRequestStopConfirm,
  clearRequestStopConfirm:
    useRecordingSessionStore.getState().clearRequestStopConfirm,
  releaseStream: useRecordingSessionStore.getState().releaseStream,
  endSession: useRecordingSessionStore.getState().endSession,
  registerInlineCapture:
    useRecordingSessionStore.getState().registerInlineCapture,
  clearInlineCapture: useRecordingSessionStore.getState().clearInlineCapture
});

export const getRecordingSessionState = () =>
  useRecordingSessionStore.getState();

/** Memoized action hook for stable reference inside components */
export const useRecordingSessionActions = () => {
  return useMemo(() => getRecordingSessionActions(), []);
};
